import { useFormContext, Controller } from 'react-hook-form';
import FormHelperText from '@mui/material/FormHelperText';

import CasDropzoneUpload, { ICustomFile, IUploadProps } from './_internal/CasDropzoneUpload';

interface Props extends Omit<IUploadProps, 'file'> {
  name: string;
  multiple?: boolean;
}

export default function CasRHFDropzoneUpload({
  name,
  multiple,
  helperText,
  accept,
  ...other
}: Props) {
  const { control, setValue, getValues } = useFormContext();

  const onDrop = (acceptedFiles: File[]) => {
    if (!acceptedFiles.length) return;

    if (multiple) {
      const files = getValues(name) || [];

      const newFiles = acceptedFiles.map((file) =>
        Object.assign(file, { preview: URL.createObjectURL(file) })
      );

      setValue(name, [...files, ...newFiles], { shouldValidate: true });
    } else {
      const file = acceptedFiles[0];
      const newFile = Object.assign(file, { preview: URL.createObjectURL(file) });
      setValue(name, newFile, { shouldValidate: true });
    }
  };

  const onRemove = (inputFile: string | ICustomFile) => {
    const files = getValues(name) || [];

    setValue(name, files && files?.filter((file: File) => file !== inputFile));
  };

  const onRemoveAll = () => setValue(name, []);

  const onDelete = () => setValue(name, null);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) =>
        multiple ? (
          <CasDropzoneUpload
            multiple
            accept={accept}
            files={field.value}
            error={!!error}
            helperText={
              (!!error || helperText) && (
                <FormHelperText error={!!error} sx={{ px: 2 }}>
                  {error ? error?.message : helperText}
                </FormHelperText>
              )
            }
            onRemove={onRemove}
            onRemoveAll={onRemoveAll}
            onDrop={onDrop}
            {...other}
          />
        ) : (
          <CasDropzoneUpload
            accept={accept}
            file={field.value}
            error={!!error}
            helperText={
              (!!error || helperText) && (
                <FormHelperText error={!!error} sx={{ px: 2 }}>
                  {error ? error?.message : helperText}
                </FormHelperText>
              )
            }
            onDrop={onDrop}
            onDelete={onDelete}
            {...other}
          />
        )
      }
    />
  );
}
